import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Link2, Unlink, RefreshCw, Loader2, ExternalLink,
  FileText, Download, CheckCircle, XCircle, AlertTriangle,
  Clock, ArrowRight, ChevronDown, ChevronUp, Plug
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDERS = {
  microsoft_forms: {
    name: 'Microsoft Forms',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
        <rect x="1" y="1" width="10" height="10" rx="1" fill="#F25022" />
        <rect x="13" y="1" width="10" height="10" rx="1" fill="#7FBA00" />
        <rect x="1" y="13" width="10" height="10" rx="1" fill="#00A4EF" />
        <rect x="13" y="13" width="10" height="10" rx="1" fill="#FFB900" />
      </svg>
    ),
    description: 'Import forms from your Microsoft 365 account',
    color: '#0078D4',
    bgLight: '#E8F4FD',
  },
  typeform: {
    name: 'Typeform',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
        <rect width="24" height="24" rx="4" fill="#262627" />
        <path d="M6 8h12v2H6V8zm0 4h8v2H6v-2z" fill="white" />
      </svg>
    ),
    description: 'Import forms from your Typeform workspace',
    color: '#262627',
    bgLight: '#F0F0F0',
  },
};

// ─── Provider Card ─────────────────────────────────────────
const ProviderCard = ({ provider, status, onConnect, onDisconnect, onBrowse, loading }) => {
  const meta = PROVIDERS[provider];
  const isConfigured = status?.configured;
  const isConnected = status?.connected;

  return (
    <Card
      className="p-6 border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors"
      data-testid={`integration-card-${provider}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: meta.bgLight }}>
            {meta.icon}
          </div>
          <div>
            <h3 className="text-base font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {meta.name}
            </h3>
            <p className="text-xs text-[#475569]">{meta.description}</p>
          </div>
        </div>

        {/* Status badge */}
        <div className="flex items-center gap-1.5">
          {!isConfigured ? (
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#FEF2F2] text-[#991B1B] font-medium" data-testid={`status-${provider}`}>
              <XCircle className="h-3 w-3" /> Not Configured
            </span>
          ) : isConnected ? (
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#F0FDF4] text-[#166534] font-medium" data-testid={`status-${provider}`}>
              <CheckCircle className="h-3 w-3" /> Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#FFFBEB] text-[#92400E] font-medium" data-testid={`status-${provider}`}>
              <AlertTriangle className="h-3 w-3" /> Disconnected
            </span>
          )}
        </div>
      </div>

      {/* Connected account info */}
      {isConnected && (
        <div className="mt-4 p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{status.account_name || 'Connected Account'}</p>
              {status.account_email && (
                <p className="text-xs text-[#475569]">{status.account_email}</p>
              )}
              {status.last_sync_at && (
                <p className="text-[10px] text-[#94A3B8] mt-1 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Last sync: {new Date(status.last_sync_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        {!isConfigured && (
          <p className="text-xs text-[#475569] bg-[#FEF2F2] p-3 rounded-lg w-full">
            This integration requires environment variables to be configured. Add the OAuth credentials for {meta.name} to activate.
          </p>
        )}

        {isConfigured && !isConnected && (
          <Button
            onClick={() => onConnect(provider)}
            disabled={loading}
            className="rounded-full"
            style={{ backgroundColor: meta.color }}
            data-testid={`connect-${provider}-btn`}
          >
            {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Link2 className="h-4 w-4 mr-1.5" />}
            Connect {meta.name}
          </Button>
        )}

        {isConnected && (
          <>
            <Button
              onClick={() => onBrowse(provider)}
              size="sm"
              className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
              data-testid={`browse-forms-${provider}-btn`}
            >
              <FileText className="h-4 w-4 mr-1.5" /> Browse Forms
            </Button>
            <Button
              onClick={() => onDisconnect(provider)}
              size="sm"
              variant="outline"
              className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
              data-testid={`disconnect-${provider}-btn`}
            >
              <Unlink className="h-4 w-4 mr-1.5" /> Disconnect
            </Button>
          </>
        )}
      </div>
    </Card>
  );
};

// ─── Form Browser Modal ────────────────────────────────────
const FormBrowser = ({ provider, forms, loading, onImport, onClose, importingId }) => {
  const meta = PROVIDERS[provider];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="form-browser-overlay">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2">
            {meta.icon}
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {meta.name} — Available Forms
            </h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="rounded-full">
            Close
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-[#1d4ed8]" />
              <p className="text-sm text-[#475569]">Fetching forms from {meta.name}...</p>
            </div>
          )}

          {!loading && forms.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-10 w-10 text-[#94A3B8] mx-auto mb-3" />
              <p className="text-sm text-[#475569]">No forms found in your {meta.name} account.</p>
            </div>
          )}

          {!loading && forms.length > 0 && (
            <div className="space-y-2" data-testid="form-browser-list">
              {forms.map((form) => (
                <div
                  key={form.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors"
                  data-testid={`form-item-${form.id}`}
                >
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-medium truncate">{form.title}</p>
                    {form.modified && (
                      <p className="text-[10px] text-[#94A3B8] mt-0.5">
                        Modified: {new Date(form.modified).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {form.url && (
                      <a href={form.url} target="_blank" rel="noopener noreferrer" className="text-[#475569] hover:text-[#1d4ed8]">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    <Button
                      size="sm"
                      onClick={() => onImport(provider, form.id, form.title)}
                      disabled={importingId === form.id}
                      className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
                      data-testid={`import-form-${form.id}-btn`}
                    >
                      {importingId === form.id ? (
                        <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Importing...</>
                      ) : (
                        <><Download className="h-3.5 w-3.5 mr-1" /> Import</>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Imported Forms Section ────────────────────────────────
const ImportedFormsSection = ({ forms, loading }) => {
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();

  if (!forms.length && !loading) return null;

  return (
    <Card className="p-6 border border-[#E2E8F0]" data-testid="imported-forms-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#1d4ed8]" />
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Imported Forms
          </h2>
          <span className="text-xs bg-[#EFF6FF] text-[#1d4ed8] px-2 py-0.5 rounded-full font-medium">
            {forms.length}
          </span>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {expanded && (
        <div className="mt-4 space-y-2">
          {forms.map((form) => (
            <div
              key={form.id}
              className="flex items-center justify-between p-3 rounded-lg border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="p-1.5 rounded" style={{ backgroundColor: PROVIDERS[form.source_type]?.bgLight || '#F0F0F0' }}>
                  {PROVIDERS[form.source_type]?.icon || <FileText className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{form.title}</p>
                  <div className="flex items-center gap-2 text-[10px] text-[#94A3B8] mt-0.5">
                    <span>{form.questions?.length || 0} questions</span>
                    <span>&middot;</span>
                    <span>Imported {new Date(form.imported_at).toLocaleDateString()}</span>
                    {form.import_warnings?.length > 0 && (
                      <>
                        <span>&middot;</span>
                        <span className="text-amber-600">{form.import_warnings.length} warnings</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {form.qr_id && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/qr-codes/${form.qr_id}/analytics`)}
                  className="rounded-full text-xs"
                >
                  <ArrowRight className="h-3 w-3 mr-1" /> View Interview
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// ─── Main Page ─────────────────────────────────────────────
export const Integrations = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [configStatus, setConfigStatus] = useState(null);
  const [importedForms, setImportedForms] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [browsingProvider, setBrowsingProvider] = useState(null);
  const [providerForms, setProviderForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [importingId, setImportingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const [statusRes, formsRes] = await Promise.all([
        axios.get(`${API}/integrations/config-status`, { headers }),
        axios.get(`${API}/integrations/imported-forms`, { headers }),
      ]);
      setConfigStatus(statusRes.data);
      setImportedForms(formsRes.data.forms || []);
    } catch {
      toast.error('Failed to load integration status');
    } finally {
      setLoadingStatus(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAll();
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    if (connected) {
      toast.success(`${PROVIDERS[connected === 'microsoft' ? 'microsoft_forms' : connected]?.name || connected} connected successfully!`);
    }
    if (error) {
      toast.error(`OAuth connection failed: ${error.replace(/_/g, ' ')}`);
    }
  }, [fetchAll, searchParams]);

  const handleConnect = useCallback(async (provider) => {
    setConnectingProvider(provider);
    try {
      const endpoint = provider === 'microsoft_forms' ? 'microsoft' : 'typeform';
      const res = await axios.get(`${API}/integrations/${endpoint}/authorize`, { headers });
      window.location.href = res.data.authorize_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to initiate ${PROVIDERS[provider]?.name} connection`);
      setConnectingProvider(null);
    }
  }, [token]);

  const handleDisconnect = useCallback(async (provider) => {
    if (!window.confirm(`Disconnect ${PROVIDERS[provider]?.name}? You can reconnect later.`)) return;
    try {
      await axios.delete(`${API}/integrations/${provider}/disconnect`, { headers });
      toast.success(`${PROVIDERS[provider]?.name} disconnected`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disconnect failed');
    }
  }, [token, fetchAll]);

  const handleBrowse = useCallback(async (provider) => {
    setBrowsingProvider(provider);
    setLoadingForms(true);
    setProviderForms([]);
    try {
      const endpoint = provider === 'microsoft_forms' ? 'microsoft' : 'typeform';
      const res = await axios.get(`${API}/integrations/${endpoint}/forms`, { headers });
      setProviderForms(res.data.forms || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch forms');
      setBrowsingProvider(null);
    } finally {
      setLoadingForms(false);
    }
  }, [token]);

  const handleImport = useCallback(async (provider, formId, formTitle) => {
    setImportingId(formId);
    try {
      const endpoint = provider === 'microsoft_forms' ? 'microsoft' : 'typeform';
      const res = await axios.post(`${API}/integrations/${endpoint}/import/${formId}`, {}, { headers, timeout: 60000 });
      const result = res.data;
      toast.success(`"${formTitle}" imported successfully!`);

      if (result.warnings?.length) {
        result.warnings.forEach(w => toast.warning(w, { duration: 8000 }));
      }

      const formsRes = await axios.get(`${API}/integrations/imported-forms`, { headers });
      setImportedForms(formsRes.data.forms || []);
      setBrowsingProvider(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImportingId(null);
    }
  }, [token]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="integrations-page">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-[#EFF6FF]">
              <Plug className="h-6 w-6 text-[#1d4ed8]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Connect Form Sources
              </h1>
              <p className="text-sm text-[#475569] mt-0.5">
                Import existing forms and power them with the AI Interview Engine
              </p>
            </div>
          </div>
        </div>

        {loadingStatus ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#1d4ed8]" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Provider cards */}
            {configStatus && Object.keys(PROVIDERS).map((provider) => (
              <ProviderCard
                key={provider}
                provider={provider}
                status={configStatus[provider]}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                onBrowse={handleBrowse}
                loading={connectingProvider === provider}
              />
            ))}

            {/* Imported forms */}
            <ImportedFormsSection forms={importedForms} loading={loadingStatus} />

            {/* Question type mapping info */}
            <Card className="p-5 border border-[#E2E8F0]" data-testid="supported-types-info">
              <h3 className="text-sm font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Supported Question Types
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {['Short Text', 'Long Text', 'Multiple Choice', 'Dropdown', 'Yes/No', 'Rating', 'Opinion Scale'].map(t => (
                  <div key={t} className="flex items-center gap-1.5 text-xs text-[#475569]">
                    <CheckCircle className="h-3 w-3 text-green-500" />{t}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-3">
                Unsupported question types (file uploads, payments, images) are marked with warnings and skipped during import.
              </p>
            </Card>
          </div>
        )}
      </main>

      {/* Form Browser overlay */}
      {browsingProvider && (
        <FormBrowser
          provider={browsingProvider}
          forms={providerForms}
          loading={loadingForms}
          onImport={handleImport}
          onClose={() => setBrowsingProvider(null)}
          importingId={importingId}
        />
      )}
    </div>
  );
};

export default Integrations;
